/**
 * Engagement letter status semantics for CRM + client portal surfaces.
 * `voided` is terminal and non-billable (ops voids test / cancelled agreements).
 */

export type EngagementLetterStatusValue =
  | 'draft'
  | 'sent'
  | 'viewed'
  | 'signed'
  | 'paid'
  | 'expired'
  | 'voided';

export const ENGAGEMENT_LETTER_STATUSES: readonly EngagementLetterStatusValue[] = [
  'draft',
  'sent',
  'viewed',
  'signed',
  'paid',
  'expired',
  'voided',
] as const;

/** Statuses that may receive a client signature. */
export const SIGNABLE_ENGAGEMENT_LETTER_STATUSES = new Set<string>(['sent', 'viewed']);

/** Terminal statuses that are never owed / never counted in unpaid totals. */
export const NON_BILLABLE_ENGAGEMENT_LETTER_STATUSES = new Set<string>([
  'paid',
  'voided',
  'expired',
]);

export function normalizeEngagementLetterStatus(
  status: unknown,
): string {
  return typeof status === 'string' ? status.trim().toLowerCase() : '';
}

export function isVoidedEngagementLetter(status: unknown): boolean {
  const s = normalizeEngagementLetterStatus(status);
  return s === 'voided' || s === 'void';
}

/** Signed-but-unpaid inventory — excludes voided/expired/paid/draft/sent/viewed. */
export function isEngagementLetterAwaitingPayment(status: unknown): boolean {
  return normalizeEngagementLetterStatus(status) === 'signed';
}

/** Fee may still be collected (or is already collected as paid). Voided/expired are out. */
export function engagementLetterCountsTowardUnpaid(status: unknown): boolean {
  const s = normalizeEngagementLetterStatus(status);
  if (!s) return false;
  if (NON_BILLABLE_ENGAGEMENT_LETTER_STATUSES.has(s)) return false;
  // Only signed (awaiting payment) counts as unpaid outstanding.
  return s === 'signed';
}

export function canSignEngagementLetterStatus(status: unknown): boolean {
  return SIGNABLE_ENGAGEMENT_LETTER_STATUSES.has(normalizeEngagementLetterStatus(status));
}

/** Statuses that may open Stripe checkout (with or without a new signature). */
export const CHECKOUT_ALLOWED_ENGAGEMENT_STATUSES = new Set(['sent', 'viewed', 'signed']);

/** Design fee / checkout must not proceed against draft/paid/void/expired. */
export function canCheckoutAgainstEngagementLetter(status: unknown): boolean {
  const s = normalizeEngagementLetterStatus(status);
  if (!s) return true; // no letter status → other portal checks apply
  if (isVoidedEngagementLetter(s)) return false;
  return CHECKOUT_ALLOWED_ENGAGEMENT_STATUSES.has(s);
}
